/**
 * AudioRecorder Service
 * Handles browser audio capture using MediaRecorder API
 * Streams audio chunks for real-time processing
 */

import { errorMessage, errorName } from '../utils/errors';

export interface AudioRecorderConfig {
  channelCount?: number;
  audioBitsPerSecond?: number;
  mimeType?: string;
  deviceId?: string;
}

export interface AudioChunk {
  data: ArrayBuffer;
  timestamp: number;
}

export type AudioRecorderEventMap = {
  'audio-chunk': AudioChunk;
  'recording-started': void;
  'recording-stopped': void;
  error: Error;
  'permission-denied': void;
};

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private isRecording = false;
  /**
   * Registered handlers. One map for five event shapes, so the stored type is
   * the widest handler (`emit` recovers the payload type from the event name).
   */
  private eventListeners = new Map<keyof AudioRecorderEventMap, Set<(...args: never[]) => void>>();

  private config: Required<Omit<AudioRecorderConfig, 'deviceId'>> & { deviceId?: string };

  constructor(config: AudioRecorderConfig = {}) {
    this.config = {
      channelCount: config.channelCount || 1, // Mono
      audioBitsPerSecond: config.audioBitsPerSecond || 64000,
      mimeType: config.mimeType || this.getBestMimeType(),
      deviceId: config.deviceId,
    };
  }

  /**
   * Get the best supported MIME type for audio recording
   */
  private getBestMimeType(): string {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'audio/webm'; // Fallback
  }

  /**
   * Check if the browser supports audio recording
   */
  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      window.MediaRecorder
    );
  }

  /**
   * Request microphone permission and start recording
   */
  async startRecording(): Promise<void> {
    if (this.isRecording) {
      console.warn('[AudioRecorder] Already recording');
      return;
    }

    if (!AudioRecorder.isSupported()) {
      const error = new Error('Audio recording not supported in this browser');
      this.emit('error', error);
      throw error;
    }

    try {
      // Request microphone access — do NOT constrain sampleRate here.
      // Opus encodes at 48 kHz internally; feeding it a pre-downsampled 16 kHz
      // signal degrades quality. Let the backend ffmpeg handle the final
      // conversion to 16 kHz PCM for Whisper.
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: this.config.channelCount,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...(this.config.deviceId ? { deviceId: { exact: this.config.deviceId } } : {}),
        },
      });

      // Log microphone info
      const audioTrack = this.audioStream.getAudioTracks()[0];
      if (audioTrack) {
        console.log('[AudioRecorder] Using microphone:', {
          label: audioTrack.label,
          settings: audioTrack.getSettings(),
          capabilities: audioTrack.getCapabilities?.(),
        });
      }

      // Create MediaRecorder
      const options: MediaRecorderOptions = {
        mimeType: this.config.mimeType,
        audioBitsPerSecond: this.config.audioBitsPerSecond,
      };

      this.mediaRecorder = new MediaRecorder(this.audioStream, options);
      this.audioChunks = [];

      // Handle data available event
      this.mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);

          // Convert blob to ArrayBuffer and emit
          event.data.arrayBuffer().then((arrayBuffer) => {
            this.emit('audio-chunk', {
              data: arrayBuffer,
              timestamp: Date.now(),
            });
          });
        }
      });

      // Handle recording stop
      this.mediaRecorder.addEventListener('stop', () => {
        console.log('[AudioRecorder] Recording stopped');
        this.emit('recording-stopped');
      });

      // Handle errors
      this.mediaRecorder.addEventListener('error', (event: Event) => {
        // MediaRecorder reports its failure on the event itself (not in the
        // standard DOM event shape), hence the local view.
        const failure = event as Event & { error?: { message?: string } };
        const error = new Error(`MediaRecorder error: ${failure.error?.message || 'Unknown'}`);
        console.error('[AudioRecorder]', error);
        this.emit('error', error);
      });

      // Start recording with time slicing (emit chunks every 250ms for more reliable chunks)
      this.mediaRecorder.start(250);
      this.isRecording = true;

      console.log('[AudioRecorder] Recording started', {
        mimeType: this.config.mimeType,
        actualSampleRate: audioTrack?.getSettings()?.sampleRate,
        channelCount: this.config.channelCount,
        audioBitsPerSecond: this.config.audioBitsPerSecond,
        actualBitsPerSecond: this.mediaRecorder.audioBitsPerSecond,
        state: this.mediaRecorder.state,
      });

      this.emit('recording-started');
    } catch (error) {
      console.error('[AudioRecorder] Failed to start recording:', error);

      if (errorName(error) === 'NotAllowedError' || errorName(error) === 'PermissionDeniedError') {
        this.emit('permission-denied');
      } else {
        this.emit('error', error instanceof Error ? error : new Error(errorMessage(error, 'Recording failed')));
      }

      throw error;
    }
  }

  /**
   * Stop recording and return the complete audio blob
   */
  async stopRecording(): Promise<Blob | null> {
    if (!this.isRecording || !this.mediaRecorder) {
      console.warn('[AudioRecorder] Not currently recording');
      return null;
    }

    return new Promise((resolve) => {
      if (!this.mediaRecorder) {
        resolve(null);
        return;
      }

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        console.warn('[AudioRecorder] Stop timeout - forcing cleanup');
        const audioBlob = new Blob(this.audioChunks, {
          type: this.config.mimeType,
        });
        this.cleanup();
        resolve(audioBlob);
      }, 3000); // 3 second timeout

      this.mediaRecorder.addEventListener(
        'stop',
        () => {
          clearTimeout(timeout);

          // Combine all chunks into a single blob
          const audioBlob = new Blob(this.audioChunks, {
            type: this.config.mimeType,
          });

          // Cleanup
          this.cleanup();

          resolve(audioBlob);
        },
        { once: true },
      );

      try {
        this.mediaRecorder.stop();
        this.isRecording = false;
      } catch (error) {
        console.error('[AudioRecorder] Error stopping recorder:', error);
        clearTimeout(timeout);
        this.cleanup();
        resolve(null);
      }
    });
  }

  /**
   * Cancel recording without returning audio data
   */
  cancelRecording(): void {
    if (!this.isRecording) {
      return;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    this.cleanup();
    this.isRecording = false;
    console.log('[AudioRecorder] Recording cancelled');
  }

  /**
   * Cleanup audio stream and resources
   */
  private cleanup(): void {
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => track.stop());
      this.audioStream = null;
    }

    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  /**
   * Check if currently recording
   */
  getIsRecording(): boolean {
    return this.isRecording;
  }

  /**
   * Get the current audio stream
   */
  getAudioStream(): MediaStream | null {
    return this.audioStream;
  }

  /**
   * Get audio level from the stream (for visualization)
   */
  createAudioLevelMonitor(): AudioLevelMonitor | null {
    if (!this.audioStream) {
      return null;
    }

    return new AudioLevelMonitor(this.audioStream);
  }

  /**
   * Event emitter - register event listener
   */
  on<K extends keyof AudioRecorderEventMap>(event: K, callback: (data: AudioRecorderEventMap[K]) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
  }

  /**
   * Event emitter - remove event listener
   */
  off<K extends keyof AudioRecorderEventMap>(event: K, callback: (data: AudioRecorderEventMap[K]) => void): void {
    this.eventListeners.get(event)?.delete(callback);
  }

  /**
   * Event emitter - emit event
   *
   * Handlers are stored under one type (the map is heterogeneous), so the payload
   * type is recovered here, where the event name says which one it is.
   */
  private emit<K extends keyof AudioRecorderEventMap>(event: K, data?: AudioRecorderEventMap[K]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      for (const handler of listeners) {
        (handler as (payload: unknown) => void)(data);
      }
    }
  }

  /**
   * Destroy the recorder and cleanup all resources
   */
  destroy(): void {
    this.cancelRecording();
    this.eventListeners.clear();
  }
}

/**
 * Audio Level Monitor
 * Provides real-time audio level for visualization
 */
export class AudioLevelMonitor {
  private audioContext: AudioContext;
  private analyser: AnalyserNode;
  private microphone: MediaStreamAudioSourceNode;
  private dataArray: Uint8Array;
  private animationFrameId: number | null = null;

  constructor(stream: MediaStream) {
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;

    this.microphone = this.audioContext.createMediaStreamSource(stream);
    this.microphone.connect(this.analyser);

    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount) as Uint8Array;
  }

  /**
   * Get current audio level (0-100)
   */
  getLevel(): number {
    this.analyser.getByteFrequencyData(this.dataArray as unknown as Uint8Array<ArrayBuffer>);

    const sum = this.dataArray.reduce((acc: number, val: number) => acc + val, 0);
    const average = sum / this.dataArray.length;

    return Math.round((average / 255) * 100);
  }

  /**
   * Start monitoring and call callback with audio level
   */
  startMonitoring(callback: (level: number) => void): void {
    const monitor = () => {
      const level = this.getLevel();
      callback(level);
      this.animationFrameId = requestAnimationFrame(monitor);
    };
    monitor();
  }

  /**
   * Stop monitoring
   */
  stopMonitoring(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopMonitoring();
    this.microphone.disconnect();
    this.audioContext.close();
  }
}
